/*
 * Сетка дня журнала.
 *
 * Ни одного цвета и ни одного размера сетки в этом файле нет — только var() из tokens.css.
 * Геометрия, которая зависит от шага сетки и числа мастеров (top, height, left, width),
 * приходит из кода отдельным стилем: её считает grid-geometry.ts, и посчитать её в CSS
 * нельзя — она зависит от данных.
 */

/* Рамка сетки. Нужна ради подсказки поверх дня: она не должна уезжать при прокрутке. */
.frame {
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

.scroll {
  position: relative;
  flex: 1;
  min-height: 0;
  background: var(--bg-workspace);
  /* Прокрутка в обе стороны: пятнадцать мастеров не помещаются по ширине, а день
     при шаге 15 — по высоте. Шкала времени и шапка мастеров при этом закреплены. */
  overflow: auto;
  overscroll-behavior: contain;
}

/*
 * Проверка различимости статусов без цвета (критерий приёмки ТЗ §2.3.3).
 * Включается адресом ?check=gray — так ссылку на проверку можно прислать в чате,
 * а в самом кабинете не заводится лишняя кнопка, которой администратор не пользуется.
 */
.checkGrayscale {
  filter: grayscale(1);
}

/*
 * ПОЛЕ СЕТКИ ЗАНИМАЕТ ВСЮ ШИРИНУ, даже когда колонок мало.
 *
 * Ширина колонки мастера упирается в потолок 300 px (токен --grid-max-column,
 * подтверждён дизайнером: шире колонка выглядит пустой). У салона с одним-двумя
 * мастерами поэтому справа оставалось больше половины экрана — голый фон без шапки
 * и без линий часов, и журнал читался как недорисованный. Колонки остаются своей
 * ширины, а шапка и линии часов продолжаются до края: справа — продолжение дня,
 * а не обрыв страницы. Ширина слоя из разметки (по колонкам) остаётся нижней
 * границей: при пятнадцати мастерах прокрутка по горизонтали работает как прежде.
 */
.layer {
  position: relative;
  min-width: 100%;
}

/* ─────────── шапка мастеров ─────────── */

.header {
  position: sticky;
  top: 0;
  z-index: var(--z-master-header);
  display: flex;
  min-width: 100%;
  height: var(--grid-master-header);
  background: var(--bg-card);
  border-bottom: 1px solid var(--border-strong);
}

/* Угол: он остаётся на месте при прокрутке в обе стороны и поэтому лежит выше всех. */
.headerCorner {
  position: sticky;
  left: 0;
  z-index: var(--z-header-corner);
  flex: none;
  width: var(--grid-time-column);
  background: var(--bg-card);
  border-right: 1px solid var(--border);
}

.headerCell {
  display: flex;
  flex: none;
  gap: 7px;
  align-items: center;
  padding: 0 var(--space-2);
  overflow: hidden;
  border-right: 1px solid var(--border);
}

.avatar {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-semibold);
  border: 1px solid var(--master-avatar-ring);
  border-radius: 50%;
}

.headerText {
  min-width: 0;
  line-height: 1.2;
}

.headerName {
  overflow: hidden;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.headerPosition {
  overflow: hidden;
  font-size: var(--font-size-xs);
  white-space: nowrap;
  text-overflow: ellipsis;
  opacity: 0.75;
}

/* Колонка «Без мастера» присутствует всегда, даже пустая (ТЗ §2.2.2). */
.headerUnassigned {
  display: flex;
  flex: none;
  align-items: center;
  width: var(--grid-unassigned-column);
  padding: 0 10px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
  background: var(--bg-unassigned);
  border-left: 1px dashed var(--border-strong);
}

/* ─────────── тело ─────────── */

.body {
  position: absolute;
  top: var(--grid-master-header);
  left: 0;
  min-width: 100%;
}

.line {
  position: absolute;
  right: 0;
  left: var(--grid-time-column);
  height: 0;
}

/* Три веса линий. Час обязан остаться заметнее получаса и в градациях серого,
   поэтому часовая линия не только темнее — она единственная непрозрачная. */
.lineHour {
  border-top: 1px solid var(--grid-line-hour);
}

.lineHalf {
  border-top: 1px solid var(--grid-line-half);
}

.lineMinor {
  border-top: 1px solid var(--grid-line-minor);
}

/*
 * Шкала времени. Закреплена слева и не уезжает никогда — при пятнадцати мастерах
 * это единственное, по чему администратор понимает, на какой час он смотрит.
 * Работает это за счёт того, что шкала — единственный элемент тела в обычном потоке:
 * карточки и колонки позиционированы абсолютно и на sticky не влияют.
 */
.scale {
  position: sticky;
  left: 0;
  z-index: var(--z-grid-scale);
  width: var(--grid-time-column);
  background: var(--bg-workspace);
  border-right: 1px solid var(--border);
}

.label {
  position: absolute;
  right: var(--space-2);
  font-variant-numeric: tabular-nums;
  line-height: 1;
  transform: translateY(-55%);
}

/* Первая подпись прижата к верху: центрировать её не по чему, сверху ничего нет. */
.labelFirst {
  transform: translateY(2px);
}

/* 11 px, как в макете, а не 11.5: шкала времени — это плотность, которую дизайнер
   сдавал эталоном, и полпикселя на строке 26 px видно. */
.labelHour {
  font-size: var(--font-size-xs-plus);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.labelHalf {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-regular);
  color: var(--text-secondary);
}

/* ─────────── линия «сейчас» ─────────── */

.nowLine {
  position: absolute;
  right: 0;
  left: var(--grid-time-column);
  z-index: var(--z-now-line);
  height: 0;
  pointer-events: none;
  border-top: 2px solid var(--now);
}

.nowBadge {
  position: absolute;
  left: var(--space-1);
  z-index: 2;
  padding: 1px 5px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  font-variant-numeric: tabular-nums;
  color: var(--on-now);
  background: var(--now);
  border-radius: var(--radius-sm);
  transform: translateY(-50%);
}

/* ─────────── колонка мастера ─────────── */

.column {
  position: absolute;
  top: 0;
  border-right: 1px solid var(--border);
}

/*
 * Нерабочее время мастера. «Не работает» и «работает и свободен» — два разных ответа
 * администратору: в первое записывать нельзя, во второе нужно (ТЗ §2.2.1).
 */
.offHours {
  position: absolute;
  right: 0;
  left: 0;
  background: repeating-linear-gradient(135deg, var(--bg-sunken) 0 4px, transparent 4px 9px);
}

/*
 * Полоса цвета мастера. Это одно из двух мест, где выбранный им цвет показывается КАК ЕСТЬ,
 * без преобразования в читаемую пару: полоса ничего не подписывает, ей контраст не нужен.
 */
.columnStripe {
  position: absolute;
  left: 0;
  width: 3px;
  opacity: 0.9;
}

/* ─────────── карточка записи ─────────── */

.card {
  position: absolute;
  left: 5px;
  display: flex;
  flex-direction: column;
  width: calc(100% - 8px);
  padding: 2px 6px;
  overflow: hidden;
  line-height: 1.25;
  text-align: left;
  background: var(--bg-card);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-sm);
}

/* Вторая карточка в паузе соседа — правая половина колонки. Обе видны, не наезжают. */
.cardInPause {
  right: 3px;
  left: 46%;
  width: auto;
}

.cardBar {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 4px;
  background: var(--status-in-progress-bar);
}

/*
 * — границы: первый канал различения —
 * СТИЛЬ И ТОЛЩИНА отделены от ЦВЕТА нарочно. Эти четыре класса переживают печать
 * на чёрно-белом принтере целиком; цвет добавляется ниже отдельными классами и является
 * третьим каналом, а не первым (ТЗ §2.3.3).
 */
.borderSolid {
  border-style: solid;
  border-width: 1px;
}

.borderDashed {
  border-style: dashed;
  border-width: 1px;
}

.borderThick {
  border-style: solid;
  border-width: 1.5px;
}

.borderThickDashed {
  border-style: dashed;
  border-width: 1.5px;
}

/* — цвет: третий канал, вспомогательный — */
.tintPending {
  border-color: var(--status-pending-fg);
}

.tintConfirmed {
  border-color: var(--border-strong);
}

.tintRunning {
  border-color: var(--accent-hover);
  box-shadow: var(--shadow-card);
}

.tintCompleted {
  border-color: var(--border-strong);
}

.tintCancelled {
  border-color: var(--text-faint);
}

.tintNoShow {
  border-color: var(--danger-hover);
}

/* — заливка: второй канал —
   Штриховки различаются углом и шагом, а не цветом: иначе в градациях серого и на
   чёрно-белой печати они сливаются и признака нет. Геометрия живёт в токенах одной
   строкой на обе темы — здесь её нельзя переопределить по недосмотру. */
.fillPlain {
  background: var(--bg-card);
}

.fillSolid {
  background: var(--status-completed-bg);
}

.fillHatchSparse {
  background: var(--status-cancelled-hatch);
}

.fillHatchDense {
  background: var(--status-no-show-hatch);
}

/* Отменённая не должна мешать увидеть, что время свободно. */
.cardFaded {
  opacity: 0.85;
}

/* У «в работе» полоса съедает левый отступ — текст сдвигается, чтобы не налезать. */
.cardWithBar {
  padding-left: 10px;
}

.cardTop {
  display: flex;
  gap: 5px;
  align-items: center;
  min-width: 0;
}

/* — значок статуса: третий канал, всегда в одном и том же углу — */
.glyph {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 13px;
  height: 13px;
  font-size: 9.5px;
  font-weight: 700;
  line-height: 1;
  border-radius: 3px;
}

.glyphPending {
  color: var(--status-pending-fg);
  background: var(--status-pending-bg);
}

.glyphRunning {
  color: var(--status-in-progress-fg);
  background: var(--status-in-progress-bg);
}

.glyphCompleted {
  color: var(--status-completed-glyph);
  background: var(--bg-card);
}

.glyphCancelled {
  color: var(--status-cancelled-fg);
  background: var(--bg-card);
}

.glyphNoShow {
  color: var(--status-no-show-fg);
  background: var(--status-no-show-bg);
}

.cardName {
  min-width: 0;
  overflow: hidden;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.cardNameDim {
  color: var(--text-dimmed);
}

.cardNameStruck {
  color: var(--status-cancelled-fg);
  text-decoration: line-through;
}

.cardFlags {
  display: flex;
  flex: none;
  gap: 3px;
  margin-left: auto;
}

.flag {
  padding: 0 3px;
  font-size: 9px;
  font-weight: 700;
  line-height: 1.4;
  color: var(--text-brand);
  background: var(--bg-sunken);
  border-radius: 3px;
}

.cardTime {
  display: flex;
  gap: 6px;
  min-width: 0;
  overflow: hidden;
  font-size: var(--font-size-2xs);
  font-variant-numeric: tabular-nums;
  color: var(--text-secondary);
  white-space: nowrap;
}

.cardTimeValue {
  flex: none;
}

.cardStatusLabel {
  min-width: 0;
  overflow: hidden;
  font-weight: var(--font-weight-semibold);
  text-overflow: ellipsis;
}

.labelPending {
  color: var(--status-pending-fg);
}

.labelRunning {
  color: var(--status-in-progress-fg);
}

.labelCompleted {
  color: var(--status-completed-fg);
}

.labelCancelled {
  color: var(--status-cancelled-fg);
}

.labelNoShow {
  color: var(--status-no-show-fg);
}

.cardService {
  overflow: hidden;
  font-size: var(--font-size-2xs);
  color: var(--text-brand);
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* ─────────── занятость, которая не запись ─────────── */

.busy {
  position: absolute;
  right: 3px;
  left: 5px;
  display: flex;
  gap: 6px;
  align-items: center;
  padding: 0 6px;
  overflow: hidden;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  line-height: 1.1;
  border-radius: var(--radius-sm);
}

/* Буфер и уборка принадлежат соседней записи: читаются как её продолжение,
   а не как самостоятельный объект — отсюда отсутствие границы. */
.busySystem {
  color: var(--text-faint);
  background: repeating-linear-gradient(135deg, var(--bg-sunken) 0 2px, transparent 2px 6px);
  border: none;
}

/* Перерыв и отсутствие самостоятельны: их можно создать, подвинуть и удалить. */
.busyOwn {
  color: var(--text-secondary);
  background: var(--busy-bg);
  border: 1px dotted var(--busy-border);
}

/* Блокировка — двойная штриховка: «сюда никого не ставить», и это видно издалека. */
.busyBlocked {
  color: var(--text-brand);
  background:
    repeating-linear-gradient(45deg, var(--busy-blocked-hatch) 0 1px, transparent 1px 7px),
    repeating-linear-gradient(-45deg, var(--busy-blocked-hatch) 0 1px, var(--busy-bg) 1px 7px);
  border: 1px solid var(--border-strong);
}

/* ─────────── удержание слота ─────────── */

.hold {
  position: absolute;
  right: 3px;
  left: 5px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 2px 7px;
  overflow: hidden;
  font-size: var(--font-size-xs);
  line-height: 1.25;
  border-radius: var(--radius-sm);
}

/*
 * Удержание выглядит незавершённым нарочно: это не запись и не блокировка, а человек,
 * который прямо сейчас вводит код из SMS. Полоски и пунктир — «это ещё не решилось».
 */
.holdOnline {
  color: var(--hold-online-accent);
  background: repeating-linear-gradient(
    90deg,
    var(--hold-online-bg) 0 6px,
    var(--bg-card) 6px 12px
  );
  border: 1.5px dashed var(--hold-online-accent);
}

.holdQuarantine {
  color: var(--hold-quarantine-fg);
  background: var(--hold-quarantine-bg);
  border: 1px dashed var(--hold-quarantine-border);
}

.holdTitle {
  overflow: hidden;
  font-weight: var(--font-weight-semibold);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.holdSub {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/*
 * Объяснение удержания по нажатию (2-2.4.1). Тёмный пузырь — тот же, что у подсказок
 * библиотеки (--tooltip-*): это ответ на действие, а не ещё один объект расписания,
 * и путать его с карточкой нельзя.
 *
 * z-index выше соседних блоков: пузырь перекрывает следующий час НАРОЧНО — он живёт
 * ровно до следующего нажатия, и прочитать его важнее, чем видеть 15:30 под ним.
 */
.holdBubble {
  position: absolute;
  right: 3px;
  left: 5px;
  z-index: 6;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 7px 9px;
  font-size: var(--font-size-xs);
  line-height: 1.35;
  color: var(--tooltip-fg);
  white-space: normal;
  background: var(--tooltip-bg);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-pop);
}

.holdBubbleTitle {
  font-weight: var(--font-weight-semibold);
}

.holdBubbleAction {
  align-self: flex-start;
  padding: 3px 9px;
  font: inherit;
  color: var(--tooltip-bg);
  cursor: pointer;
  background: var(--tooltip-fg);
  border: 0;
  border-radius: var(--radius-sm);
}

/* ─────────── пауза составной услуги ─────────── */

.pause {
  position: absolute;
  right: 3px;
  left: 5px;
  display: flex;
  align-items: center;
  padding: 0 7px;
  overflow: hidden;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--pause-fg);
  white-space: nowrap;
  background: var(--pause-bg);
  border: 1px dashed var(--pause-border);
  border-radius: var(--radius-sm);
}

/* ─────────── колонка «Без мастера» ─────────── */

.unassigned {
  position: absolute;
  top: 0;
  width: var(--grid-unassigned-column);
  background: var(--bg-unassigned);
  border-left: 1px dashed var(--border-strong);
}

.unassignedCard {
  position: absolute;
  right: 5px;
  left: 5px;
  padding: 2px 6px;
  overflow: hidden;
  line-height: 1.25;
  text-align: left;
  background: var(--bg-card);
  border: 1.5px solid var(--unassigned-accent);
  border-radius: var(--radius-sm);
}

.unassignedName {
  overflow: hidden;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.unassignedTime {
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-semibold);
  color: var(--unassigned-accent);
}

/* Пустая колонка подписывается, а не исчезает: «пусто» здесь — это ответ. */
.unassignedEmpty {
  position: absolute;
  top: var(--space-4);
  right: 8px;
  left: 8px;
  font-size: var(--font-size-xs);
  color: var(--text-faint);
  text-align: center;
}

/* ─────────── ненавязчивая подсказка поверх готовой сетки ─────────── */

.hint {
  position: absolute;
  top: calc(var(--grid-master-header) + var(--space-3));
  left: calc(var(--grid-time-column) + var(--space-3));
  z-index: 2;
  max-width: 320px;
  padding: 10px 14px;
  font-size: var(--font-size-md);
  color: var(--text-secondary);
  pointer-events: none;
  background: var(--bg-card);
  border: 1px dashed var(--border-strong);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-card);
}

/* ─────────── действия в сетке (П0) ─────────── */

/*
 * Карточка-кнопка. Кнопка приносит с собой шрифт браузера, свои отступы и своё
 * выравнивание — всё это снимается, чтобы карточка осталась ровно той же, что была
 * до того, как стала кликаемой. Меняется только курсор и поведение с клавиатуры.
 */
.cardButton {
  margin: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  appearance: none;
}

.cardButton:hover {
  box-shadow: var(--shadow-card);
}

.cardButton:focus-visible {
  outline: var(--focus-ring-width) solid var(--focus-ring-color);
  outline-offset: 1px;
}

/*
 * Запись, чья панель открыта. Обводка, а не заливка: заливка — это канал различения
 * статуса, и занимать её выделением значит сделать «отменён» и «выбран» неразличимыми.
 */
.cardSelected {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: 1px;
}

/* Свободное место кликабельно: курсор «ячейка» — обещание, что сюда можно записать. */
.columnPickable {
  cursor: cell;
}

/*
 * Изменения выключены (офлайн, технические работы). Курсор говорит это ДО нажатия:
 * кнопка, которая нажимается и отвечает ошибкой, — худший способ сообщить об отсутствии
 * связи (ТЗ §2.11.4).
 */
.columnLocked {
  cursor: not-allowed;
}

.unassignedCardOpen {
  cursor: pointer;
}

.unassignedCardOpen:focus-visible {
  outline: var(--focus-ring-width) solid var(--focus-ring-color);
  outline-offset: 1px;
}
